/**
 * Blog Monitor (DWA Internal → Web).
 *
 * Monitors client community WordPress sites' RSS feeds (<domain>/feed/) and
 * shows, per site: the most recent post's date + title (linked to the post),
 * with the site name linked to its homepage. Care levels / respite shown as
 * chips. A client selector switches between monitored clients (PLC first).
 *
 * Date check: a date input at the top drives a traffic light per site,
 * comparing each site's latest post date to the entered date —
 *   green  = latest post is on or after the date (fresh)
 *   amber  = latest post is before the date (stale)
 *   red    = no post available (empty feed / unreachable / error)
 * The comparison is client-side against the already-fetched snapshot.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchBlogMonitor,
  WEB_MONITOR_CLIENTS,
  type BlogMonitorResponse,
  type BlogMonitorSite,
  type BackendClientId,
} from '../services/dashboardApi';
import type { ScorecardTheme } from './ScorecardView';

export interface BlogMonitorProps {
  theme: ScorecardTheme;
}

type Light = 'green' | 'amber' | 'red';

const AMBER = '#f59e0b';

// ─── Date helpers (compared as local YYYY-MM-DD) ───────────────────────────

function todayLocalYMD(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function localYMD(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** Traffic light for a site vs the target date. No post → red;
 * post on/after target → green; post before target → amber. */
function trafficLight(site: BlogMonitorSite, target: string): Light {
  const postDay = localYMD(site.latest_post_at);
  if (!postDay) return 'red';
  // String compare of YYYY-MM-DD is chronological.
  return postDay >= target ? 'green' : 'amber';
}

function lightColor(light: Light, theme: ScorecardTheme): string {
  if (light === 'green') return theme.positive;
  if (light === 'amber') return AMBER;
  return theme.negative;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return '—'; }
}

function ageDays(iso: string | null): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const days = Math.floor((Date.now() - then) / 86400_000);
  if (days <= 0) return 'today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

/** Human reason for a red (no-post) site. */
function noPostReason(site: BlogMonitorSite): string {
  switch (site.status) {
    case 'no_posts': return 'Feed reachable, no posts';
    case 'error': return site.error || 'Feed error';
    case 'unreachable': return site.error || `Unreachable (HTTP ${site.http_status ?? '—'})`;
    case 'pending': return 'Awaiting first check';
    default: return 'No post available';
  }
}

// ─── Chips ────────────────────────────────────────────────────────────────

const Chip: React.FC<{ text: string; theme: ScorecardTheme; tone?: 'default' | 'accent' }> = ({ text, theme, tone = 'default' }) => (
  <span style={{
    fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.03em',
    padding: '0.1rem 0.4rem', borderRadius: 3,
    background: tone === 'accent' ? `${theme.accent}22` : theme.pageBg,
    color: tone === 'accent' ? theme.sectionTitle : theme.textSecondary,
    border: `1px solid ${theme.cardBorder}`, whiteSpace: 'nowrap',
  }}>{text}</span>
);

// ─── Site card ────────────────────────────────────────────────────────────

const SiteCard: React.FC<{ site: BlogMonitorSite; targetDate: string; theme: ScorecardTheme }> = ({ site, targetDate, theme }) => {
  const light = trafficLight(site, targetDate);
  const color = lightColor(light, theme);
  const hasPost = !!site.latest_post_at;
  const age = ageDays(site.latest_post_at);
  return (
    <div style={{
      background: theme.cardBg,
      border: `1px solid ${light === 'red' ? color : theme.cardBorder}`,
      boxShadow: theme.cardShadow, borderRadius: 4, padding: '0.9rem 1rem',
      display: 'flex', flexDirection: 'column', gap: '0.55rem',
    }}>
      {/* Header: site name → homepage + traffic light */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem' }}>
        {site.homepage_url ? (
          <a href={site.homepage_url} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: '0.92rem', fontWeight: 700, color: theme.textPrimary, textDecoration: 'none', lineHeight: 1.25 }}
            onMouseEnter={(e) => (e.currentTarget.style.color = theme.accent)}
            onMouseLeave={(e) => (e.currentTarget.style.color = theme.textPrimary)}
          >{site.name} ↗</a>
        ) : (
          <span style={{ fontSize: '0.92rem', fontWeight: 700, color: theme.textPrimary }}>{site.name}</span>
        )}
        <span title={light} style={{ flexShrink: 0, marginTop: 4, width: 10, height: 10, borderRadius: '50%', background: color }} />
      </div>

      {/* Chips: care levels + respite */}
      {(site.care_levels || site.respite) && (
        <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
          {site.care_levels && <Chip text={site.care_levels} theme={theme} />}
          {site.respite && <Chip text="Respite" theme={theme} tone="accent" />}
        </div>
      )}

      {/* Latest post (always shown when a post exists, regardless of light) */}
      <div style={{ borderTop: `1px solid ${theme.rowDivider}`, paddingTop: '0.5rem' }}>
        {hasPost ? (
          <>
            <div style={{ fontSize: '0.7rem', color: light === 'amber' ? color : theme.textMuted, marginBottom: '0.2rem', fontWeight: light === 'amber' ? 600 : 400 }}>
              {fmtDate(site.latest_post_at)}{age && <span style={{ marginLeft: '0.4rem' }}>· {age}</span>}
              {light === 'amber' && <span style={{ marginLeft: '0.4rem' }}>· before target</span>}
            </div>
            {site.latest_post_url ? (
              <a href={site.latest_post_url} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: '0.85rem', color: theme.textPrimary, textDecoration: 'none', lineHeight: 1.35 }}
                onMouseEnter={(e) => (e.currentTarget.style.color = theme.accent)}
                onMouseLeave={(e) => (e.currentTarget.style.color = theme.textPrimary)}
              >{site.latest_post_title || '(untitled post)'}</a>
            ) : (
              <span style={{ fontSize: '0.85rem', color: theme.textPrimary }}>{site.latest_post_title || '(untitled post)'}</span>
            )}
          </>
        ) : (
          <div style={{ fontSize: '0.82rem', color, fontWeight: 500 }}>{noPostReason(site)}</div>
        )}
      </div>
    </div>
  );
};

// ─── Legend ───────────────────────────────────────────────────────────────

const Legend: React.FC<{ theme: ScorecardTheme }> = ({ theme }) => (
  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.72rem', color: theme.textSecondary }}>
    {([['On/after date', theme.positive], ['Before date', AMBER], ['No posts', theme.negative]] as [string, string][]).map(([label, c]) => (
      <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: c, display: 'inline-block' }} />
        {label}
      </span>
    ))}
  </div>
);

// ─── Main view ────────────────────────────────────────────────────────────

export const BlogMonitorView: React.FC<BlogMonitorProps> = ({ theme }) => {
  const [client, setClient] = useState<BackendClientId>(WEB_MONITOR_CLIENTS[0].key);
  const [targetDate, setTargetDate] = useState<string>(todayLocalYMD);
  const [data, setData] = useState<BlogMonitorResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchBlogMonitor(client));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => { load(); }, [load]);

  // Group sites by community_group, preserving backend order (already sorted).
  const groups = useMemo(() => {
    const out: { name: string; sites: BlogMonitorSite[] }[] = [];
    const idx = new Map<string, number>();
    for (const s of data?.sites ?? []) {
      const g = s.community_group || 'Other';
      if (!idx.has(g)) { idx.set(g, out.length); out.push({ name: g, sites: [] }); }
      out[idx.get(g)!].sites.push(s);
    }
    return out;
  }, [data]);

  const counts = useMemo(() => {
    const c = { green: 0, amber: 0, red: 0 };
    for (const s of data?.sites ?? []) c[trafficLight(s, targetDate)] += 1;
    return c;
  }, [data, targetDate]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: theme.textPrimary, margin: 0 }}>Blog Monitor</h2>
          <p style={{ fontSize: '0.8rem', color: theme.textMuted, marginTop: '0.25rem' }}>
            Latest blog post per community site (RSS), checked against a date.
            {data && <> · <span style={{ color: theme.positive }}>{counts.green} on/after</span> · <span style={{ color: AMBER }}>{counts.amber} before</span> · <span style={{ color: theme.negative }}>{counts.red} no posts</span></>}
            {data?.snapshot_at && <> · updated {new Date(data.snapshot_at).toLocaleString()}</>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: '0.72rem', color: theme.textSecondary, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            Check date
            <input
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value || todayLocalYMD())}
              style={{
                fontSize: '0.82rem', padding: '0.3rem 0.45rem', borderRadius: 4,
                border: `1px solid ${theme.cardBorder}`, background: theme.cardBg,
                color: theme.textPrimary, fontFamily: 'inherit', cursor: 'pointer',
              }}
            />
          </label>
          <select
            value={client}
            onChange={(e) => setClient(e.target.value as BackendClientId)}
            style={{
              fontSize: '0.82rem', padding: '0.35rem 0.55rem', borderRadius: 4,
              border: `1px solid ${theme.cardBorder}`, background: theme.cardBg,
              color: theme.textPrimary, fontFamily: 'inherit', cursor: 'pointer',
            }}
          >
            {WEB_MONITOR_CLIENTS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <button
            type="button" onClick={load} disabled={loading}
            style={{
              fontSize: '0.78rem', fontWeight: 600, padding: '0.4rem 0.85rem',
              borderRadius: 4, cursor: loading ? 'default' : 'pointer', fontFamily: 'inherit',
              background: 'transparent', color: theme.textSecondary,
              border: `1px solid ${theme.cardBorder}`, opacity: loading ? 0.6 : 1,
            }}
          >{loading ? 'Loading…' : 'Refresh'}</button>
        </div>
      </div>

      <Legend theme={theme} />

      {error && (
        <div style={{
          background: theme.cardBg, border: `1px solid ${theme.negative}`,
          borderRadius: 4, padding: '1rem 1.25rem', color: theme.negative, fontSize: '0.85rem',
        }}>Failed to load Blog Monitor: {error}</div>
      )}

      {!error && data && data.site_count === 0 && !loading && (
        <div style={{
          background: theme.cardBg, border: `1px solid ${theme.cardBorder}`,
          borderRadius: 4, padding: '2rem', textAlign: 'center', color: theme.textMuted,
        }}>No sites configured for this client yet.</div>
      )}

      {!error && groups.map((group) => (
        <div key={group.name}>
          <h3 style={{
            fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.15em',
            textTransform: 'uppercase', color: theme.sectionTitle,
            margin: '0 0 0.6rem', paddingBottom: '0.4rem',
            borderBottom: `1px solid ${theme.sectionDivider}`,
          }}>{group.name} <span style={{ color: theme.textMuted }}>· {group.sites.length}</span></h3>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.85rem',
          }}>
            {group.sites.map((s) => <SiteCard key={s.site_id} site={s} targetDate={targetDate} theme={theme} />)}
          </div>
        </div>
      ))}
    </div>
  );
};

export default BlogMonitorView;
